// src/services/permissions/capRaisePermissionService.js
// CapRaise-specific permission service

import { supabase } from '../../config';
import { ROLES } from '../constants/roleConstants';
import { checkSuperAdmin } from './adminPermissionService';

/**
 * Get user's role for a specific cap raise round
 * @param {string} userId - User ID
 * @param {string} roundId - Round ID
 * @returns {Promise<string|null>} User's role or null
 */
export const getCapRaiseRole = async (userId, roundId) => {
    try {
        const isSuperAdmin = await checkSuperAdmin(userId);
        if (isSuperAdmin) return ROLES.SUPER_ADMIN;

        const { data, error } = await supabase
            .from('capraise_users')
            .select('role')
            .eq('user_id', userId)
            .eq('round_id', roundId)
            .single();
        
        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }
        
        return data?.role || null;
    } catch (error) {
        console.error('Error getting capraise role:', error);
        return null;
    }
};

/**
 * Get all rounds for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of rounds
 */
export const getUserRounds = async (userId) => {
    try {
        const isSuperAdmin = await checkSuperAdmin(userId);
        
        if (isSuperAdmin) {
            const { data, error } = await supabase
                .from('capraise_rounds')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            return data || [];
        }

        const { data, error } = await supabase
            .from('capraise_users')
            .select(`
                *,
                round:capraise_rounds(*)
            `)
            .eq('user_id', userId);
        
        if (error) throw error;
        return data?.map(item => item.round).filter(Boolean) || [];
    } catch (error) {
        console.error('Error getting user rounds:', error);
        return [];
    }
};

/**
 * Assign user to cap raise round
 * @param {string} userId - User ID
 * @param {string} roundId - Round ID
 * @param {string} role - Role to assign
 * @param {string} invitedBy - User ID who invited
 * @returns {Promise<Object>} Result object
 */
export const assignCapRaiseRole = async (userId, roundId, role, invitedBy = null) => {
    try {
        // Check if user already has a role in this round
        const { data: existingUser, error: selectError } = await supabase
            .from('capraise_users')
            .select('id')
            .eq('user_id', userId)
            .eq('round_id', roundId)
            .maybeSingle(); // Use maybeSingle() instead of single() to avoid error when no record exists

        if (selectError) {
            console.error('Error checking existing capraise user:', selectError);
            throw selectError;
        }

        if (existingUser) {
            // Update existing role
            const { data, error } = await supabase
                .from('capraise_users')
                .update({ role, status: 'active' })
                .eq('user_id', userId)
                .eq('round_id', roundId);
            
            if (error) throw error;
            return { success: true, data };
        } else {
            // Create new role assignment
            const { data, error } = await supabase
                .from('capraise_users')
                .insert({
                    user_id: userId,
                    round_id: roundId,
                    role,
                    invited_by: invitedBy,
                    status: 'active'
                });
            
            if (error) throw error;
            return { success: true, data };
        }
    } catch (error) {
        console.error('Error assigning capraise role:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Get all users for a round
 * @param {string} roundId - Round ID
 * @returns {Promise<Array>} Array of users with roles
 */
export const getRoundUsers = async (roundId) => {
    try {
        const { data, error } = await supabase
            .from('capraise_users')
            .select(`
                *,
                user:user_profiles(*)
            `)
            .eq('round_id', roundId)
            .order('invited_at', { ascending: false });
        
        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error getting round users:', error);
        return [];
    }
};

/**
 * Remove user from round
 * @param {string} userId - User ID
 * @param {string} roundId - Round ID
 * @returns {Promise<Object>} Result object
 */
export const removeCapRaiseUser = async (userId, roundId) => {
    try {
        const { error } = await supabase
            .from('capraise_users')
            .delete()
            .eq('user_id', userId)
            .eq('round_id', roundId);
        
        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Error removing capraise user:', error);
        return { success: false, error: error.message };
    }
};
